/* Yummy Gummy — PDP Interactive Ingredients.
   Clicking a name toggles its own pre-rendered description block and
   swaps the single right-column <img>'s src/srcset/alt from data-image-*
   attributes carried on the trigger. Descriptions are never built from
   strings here (only visibility is toggled) since the rich-text HTML
   already lives in the DOM per ingredient.

   Every step below always operates on the FULL descs list (hide
   everything except the target) rather than tracking a single "old"
   element — an earlier version tracked oldDesc via a `.is-active`
   lookup taken AFTER the class was already cleared, so it was always
   null by the time the hide step ran: the previously-open description
   kept hidden=false (invisible via opacity:0, but still occupying
   layout height) forever, showing up as a permanent gap in the list.
   Operating on the whole set removes that dependency on lookup timing
   entirely, so it also stays correct under rapid re-clicks. */
(function () {
  'use strict';

  function initOne(wrap) {
    if (!wrap || wrap.__pdpIiBound) return;
    wrap.__pdpIiBound = true;

    var triggers = Array.prototype.slice.call(wrap.querySelectorAll('[data-pdp-ii-trigger]'));
    var descs = Array.prototype.slice.call(wrap.querySelectorAll('[data-pdp-ii-desc]'));
    var image = wrap.querySelector('[data-pdp-ii-image]');
    if (!triggers.length || !image) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The left "THE INGREDIENTS" card whose background tints to the active
    // ingredient's image colour.
    var card = wrap.closest('.pdp-ii') ? wrap.closest('.pdp-ii').querySelector('.pdp-ii__card') : null;
    if (!card) card = document.querySelector('.pdp-ii__card');

    // ---- Dynamic card tint: average the active image's pixels and use a very
    // light version of that colour as the card background, so each ingredient
    // (and each product's differently-coloured artwork) gives the card its own
    // matching shade. Cached per src so a re-loop doesn't recompute. ----
    var tintCache = {};
    function applyTint(rgb) {
      if (!card || !rgb) return;
      // Wash of the dominant colour mixed into white. ~28% colour so the shade
      // is clearly visible (per request — the old 14% was too faint) while the
      // dark ingredient text on top stays readable.
      var MIX = 0.28;
      var r = Math.round(rgb[0] * MIX + 255 * (1 - MIX));
      var g = Math.round(rgb[1] * MIX + 255 * (1 - MIX));
      var b = Math.round(rgb[2] * MIX + 255 * (1 - MIX));
      card.style.backgroundColor = 'rgb(' + r + ',' + g + ',' + b + ')';
    }
    function tintFromImage(src) {
      if (!card || !src) return;
      if (tintCache[src]) { applyTint(tintCache[src]); return; }
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        try {
          var s = 24; // tiny sample canvas — fast, good enough for an average
          var cv = document.createElement('canvas');
          cv.width = s; cv.height = s;
          var ctx = cv.getContext('2d');
          ctx.drawImage(probe, 0, 0, s, s);
          var data = ctx.getImageData(0, 0, s, s).data;
          var rt = 0, gt = 0, bt = 0, n = 0;
          for (var i = 0; i < data.length; i += 4) {
            var a = data[i + 3];
            if (a < 200) continue;                 // skip transparent
            var rr = data[i], gg = data[i + 1], bb = data[i + 2];
            // Skip near-white/near-black so the wash follows the real hue, not
            // the artwork's white background.
            if (rr > 244 && gg > 244 && bb > 244) continue;
            if (rr < 12 && gg < 12 && bb < 12) continue;
            rt += rr; gt += gg; bt += bb; n++;
          }
          if (!n) return;
          var rgb = [Math.round(rt / n), Math.round(gt / n), Math.round(bt / n)];
          tintCache[src] = rgb;
          applyTint(rgb);
        } catch (e) { /* CORS or canvas issue — leave the default card colour */ }
      };
      probe.src = src;
    }

    function swapImage(trigger) {
      image.classList.remove('is-loaded');
      var src = trigger.getAttribute('data-image');
      /* An <img src=""> is treated by browsers as a request to the
         CURRENT document URL, not "no image" — only assign src/srcset
         when a real value exists, otherwise clear both via removeAttribute. */
      if (src) {
        image.src = src;
        image.srcset = trigger.getAttribute('data-image-srcset') || '';
      } else {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
      }
      image.alt = trigger.getAttribute('data-image-alt') || '';
      // Tint the card to this image's colour.
      if (src) tintFromImage(src);
      window.requestAnimationFrame(function () {
        image.classList.add('is-loaded');
      });
    }

    function showOnly(newDesc) {
      descs.forEach(function (d) {
        if (d === newDesc) return;
        if (!d.classList.contains('is-active')) { d.hidden = true; return; }
        // Animate the previously-open one CLOSED, then hide it once the
        // collapse transition finishes (so the height/opacity animation runs
        // instead of snapping shut via the hidden attribute).
        d.classList.remove('is-active');
        var onEnd = function (e) {
          if (e.target !== d) return;
          if (!d.classList.contains('is-active')) d.hidden = true;
          d.removeEventListener('transitionend', onEnd);
        };
        d.addEventListener('transitionend', onEnd);
      });
      if (newDesc) {
        newDesc.hidden = false;
        // Two frames so the browser paints the collapsed state before we add
        // is-active — otherwise the open transition wouldn't run.
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            newDesc.classList.add('is-active');
          });
        });
      }
    }

    function activate(trigger) {
      if (trigger.classList.contains('is-active')) return;
      var index = trigger.getAttribute('data-index');
      var newDesc = wrap.querySelector('[data-pdp-ii-desc][data-index="' + index + '"]');

      triggers.forEach(function (t) {
        var active = t === trigger;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      if (reduceMotion) {
        showOnly(newDesc);
        swapImage(trigger);
        return;
      }

      descs.forEach(function (d) {
        if (d !== newDesc) d.classList.remove('is-active');
      });
      image.classList.remove('is-loaded');

      window.setTimeout(function () {
        showOnly(newDesc);
        swapImage(trigger);
      }, 180);
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        // A manual pick restarts the auto-loop timer from this ingredient so
        // it doesn't immediately jump away.
        userPickedAt = Date.now();
        activate(trigger);
      });
    });

    // ---- Auto-loop: cycle Biotin → Vitamin C → Zinc → … on a timer, so the
    // section animates on its own. Pauses while the pointer is over the section
    // (so a reader isn't yanked to the next item), on tab-hidden, and briefly
    // after a manual pick; respects reduced-motion. ----
    var userPickedAt = 0;
    if (triggers.length > 1 && !reduceMotion) {
      var LOOP_MS = 4000;
      var hovering = false;
      wrap.addEventListener('mouseenter', function () { hovering = true; });
      wrap.addEventListener('mouseleave', function () { hovering = false; });
      wrap.addEventListener('focusin', function () { hovering = true; });
      wrap.addEventListener('focusout', function () { hovering = false; });

      window.setInterval(function () {
        if (hovering) return;
        if (document.hidden) return;
        // Hold a beat after a manual selection.
        if (Date.now() - userPickedAt < LOOP_MS) return;
        var activeIdx = -1;
        triggers.forEach(function (t, i) { if (t.classList.contains('is-active')) activeIdx = i; });
        var next = triggers[(activeIdx + 1) % triggers.length];
        if (next) activate(next);
      }, LOOP_MS);
    }

    // Tint the card from the first (already-active) image on load.
    var firstSrc = triggers[0] && triggers[0].getAttribute('data-image');
    if (firstSrc) tintFromImage(firstSrc);

    /* The "View all ingredients" button used to forward a click to the
       Supplement Facts trigger. It now carries [data-pdp-ing-trigger]
       itself (see sections/pdp-ingredients-interactive.liquid), and
       assets/pdp-ingredients-drawer.js binds every element with that
       attribute via querySelectorAll — so the All Ingredients drawer opens
       with no forwarding needed here. */

    /* ---- Stable card height on mobile -------------------------------------
       The card is auto-height, so opening a taller description than the one
       before it grows the box and shoves everything below it down — the list
       visibly jumps on every tap. Measure each description once, reserve the
       tallest, and the box then stays put whichever ingredient is open.

       Measured rather than hardcoded because the count and wording of the
       ingredients differ per product, so the right height does too.

       Mobile only. On desktop the two columns are a stretch grid, so the image
       card beside this one already sets the row height and the text card fills
       it — reserving here as well would make the text card overshoot and the
       two columns would no longer end on the same line.

       Re-measured on resize/orientation change, since width changes how many
       lines each description takes. */
    var mobileQuery = window.matchMedia('(max-width: 989px)');

    function reserveTallestDesc() {
      if (!card) return;
      if (!mobileQuery.matches) { card.style.minHeight = ''; return; }

      // Clear first so a previous reservation can't inflate the measurement.
      card.style.minHeight = '';

      // Measure each description at its natural height. A closed one is
      // hidden AND collapsed to a 0fr grid row, so it has to be temporarily
      // un-hidden and un-clipped to read a real height — done in one
      // synchronous pass with transitions off, so nothing is ever painted
      // mid-measurement and no animation fires.
      var tallest = 0;
      descs.forEach(function (d) {
        var inner = d.querySelector('.pdp-ii__desc-inner');
        if (!inner) return;
        var wasHidden = d.hidden;
        var wasActive = d.classList.contains('is-active');
        var prevTransition = d.style.transition;
        var prevRows = d.style.gridTemplateRows;
        d.style.transition = 'none';
        d.hidden = false;
        // Measure in the OPEN state: .is-active adds a bottom padding to the
        // inner element (18px on mobile), which counts toward the height the
        // card has to reserve. Measuring without it under-reserved by exactly
        // that padding, which is what still showed as a jump on the last
        // accordion to open.
        d.classList.add('is-active');
        d.style.gridTemplateRows = '1fr';
        var h = inner.getBoundingClientRect().height;
        d.style.gridTemplateRows = prevRows;
        if (!wasActive) d.classList.remove('is-active');
        d.hidden = wasHidden;
        // Force a reflow before restoring the transition so the temporary
        // change can't animate when the real value comes back.
        void d.offsetHeight;
        d.style.transition = prevTransition;
        if (h > tallest) tallest = h;
      });
      if (!tallest) return;

      var open = wrap.querySelector('.pdp-ii__desc.is-active .pdp-ii__desc-inner');
      var current = open ? open.getBoundingClientRect().height : 0;

      /* A little slack on top of the tallest measured description, to absorb
         sub-pixel differences between the measuring pass and the real open
         state (wrapped text can round a pixel either way per line).

         It scales with the ingredient COUNT rather than being a flat number:
         a longer list has more rows whose rounding can accumulate, so a
         product with eight ingredients gets more slack than one with three —
         automatically, with no per-product value to maintain. Capped so a very
         long list can't reserve a wastefully tall card. */
      var slack = Math.min(6 + descs.length * 2, 28);

      // The card's measured height already includes whichever description is
      // open, so reserve only the DIFFERENCE up to the tallest one. Measuring
      // with minHeight cleared (above) keeps this idempotent — repeated calls
      // on resize recompute from the natural height rather than compounding.
      var extra = tallest - current + slack;
      if (extra > 0) card.style.minHeight = card.getBoundingClientRect().height + extra + 'px';
    }

    // Fonts and rich-text images can land after first paint and change how
    // tall a description runs, so measure again once everything has loaded.
    reserveTallestDesc();
    window.addEventListener('load', reserveTallestDesc);
    window.addEventListener('resize', reserveTallestDesc);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-pdp-ii]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
